import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

/* ---------------------------------------------------------------------------
   One overlay primitive for the whole app (UX item 6, sol review finding H8).

   Before this file every surface wrote its own Escape handler, its own focus
   move and its own backdrop tap, and none of them made the page behind the
   overlay inert — so a keyboard or screen-reader user could Tab straight onto
   the app under a modal, and two overlays could stack with no coordination.

   The primitive owns the contract:
     - focus moves into the panel on open and comes back to the opener on close
     - Tab is trapped inside the panel (dialogs only; menus keep their own
       roving focus)
     - Escape closes (window capture, so a child handler cannot swallow it)
     - everything OUTSIDE the overlay is `inert` + `aria-hidden` while it is up
     - the page behind does not scroll (dialogs)
     - ONE overlay at a time: the slot is handed over in order — the holder
       releases, its cleanups run, and the next claim is then granted. The
       pairing ceremony and the tour both take it, so whichever mounts first
       holds it and the other waits; nothing preempts anything, because a
       preempted overlay's teardown used to revert the new holder's inert
       background and scroll lock.
   --------------------------------------------------------------------------- */

export const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/* ---------------------------------------------------------------------------
   The slot: one exclusive overlay at a time, with a queue for the ones that
   must both be seen (ceremony + tour).
   --------------------------------------------------------------------------- */

interface SlotClaim {
  key: symbol;
  grant: () => void;
}

let slotHolder: SlotClaim | null = null;
const slotQueue: SlotClaim[] = [];

function claimSlot(claim: SlotClaim): () => void {
  /* NO PREEMPTION. Taking the slot off a live holder granted the new overlay
     before the old one's behaviour had cleaned up, so for a frame (and, for
     the ceremony's 320ms exit, for the whole fade) two overlays each owned a
     focus trap, an Escape listener and the page's inert/scroll state — and
     the old one's teardown then reverted the new one's, leaving the app
     unlocked behind a modal. Everything queues: the holder releases first,
     its cleanups run, and only then is the next claim granted. */
  if (!slotHolder) {
    slotHolder = claim;
    claim.grant();
  } else {
    slotQueue.push(claim);
  }
  return () => releaseSlot(claim);
}

function releaseSlot(claim: SlotClaim): void {
  if (slotHolder?.key === claim.key) {
    slotHolder = null;
    const next = slotQueue.shift();
    if (next) {
      slotHolder = next;
      next.grant();
    }
    return;
  }
  const i = slotQueue.findIndex((c) => c.key === claim.key);
  if (i >= 0) slotQueue.splice(i, 1);
}

/** Test seam: how many overlays are holding or waiting for the slot. */
export function overlaySlotSize(): number {
  return (slotHolder ? 1 : 0) + slotQueue.length;
}

/* ---------------------------------------------------------------------------
   Behaviour: focus, trap, Escape, inert background, scroll lock, focus restore.
   --------------------------------------------------------------------------- */

export interface OverlayBehaviour {
  /** Where focus goes on open. Default: the first focusable in the panel. */
  initialFocus?: string;
  /** Selector (document-scoped) used when the opener unmounted while open. */
  restoreFocusTo?: string;
  /** Trap Tab inside the panel. Default true for dialogs, false for menus. */
  trap?: boolean;
  /** Lock body scroll. Default: true for portal overlays. */
  lockScroll?: boolean;
  /** Make everything outside the overlay inert + aria-hidden. */
  inertBackground?: boolean;
}

interface MutedEl {
  el: HTMLElement;
  inert: string | null;
  hidden: string | null;
}

function useOverlayBehaviour(
  active: boolean,
  panelRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  opts: OverlayBehaviour,
): void {
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return;
    const {
      initialFocus,
      restoreFocusTo,
      trap = true,
      lockScroll = true,
      inertBackground = true,
    } = optsRef.current;

    const panel = panelRef.current;
    const root = panel?.closest<HTMLElement>('[data-pear-overlay-root]') ?? panel;
    const prevFocus = document.activeElement as HTMLElement | null;

    /* Initial focus — never steal it from something already inside the panel
       (a textarea with autoFocus is a legitimate first stop). */
    if (panel && !panel.contains(document.activeElement)) {
      const target =
        (initialFocus ? panel.querySelector<HTMLElement>(initialFocus) : null) ??
        panel.querySelector<HTMLElement>(FOCUSABLE) ??
        panel;
      target?.focus?.();
    }

    /* Background inertness: everything else on <body> goes quiet. Previous
       attribute values are recorded so a surface that was already hidden
       comes back exactly as it was. */
    const muted: MutedEl[] = [];
    if (inertBackground) {
      for (const child of Array.from(document.body.children)) {
        if (root && (child === root || child.contains(root))) continue;
        const el = child as HTMLElement;
        muted.push({ el, inert: el.getAttribute('inert'), hidden: el.getAttribute('aria-hidden') });
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
      }
    }

    let prevOverflow = '';
    let prevPad = '';
    if (lockScroll) {
      prevOverflow = document.body.style.overflow;
      prevPad = document.body.style.paddingRight;
      const gap = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (gap > 0) document.body.style.paddingRight = `${gap}px`;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!e.defaultPrevented) onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !trap || !panel) return;
      const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (n) => !n.hasAttribute('disabled'),
      );
      if (nodes.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;
      /* The PANEL is focusable too (tabIndex -1) and is a legitimate stop, so
         it counts as OUTSIDE the control list: Shift+Tab from the panel must
         wrap to the last control, not hand focus to whatever sits behind the
         dialog. */
      const inside = activeEl ? activeEl !== panel && panel.contains(activeEl) : false;
      if (e.shiftKey && (!inside || activeEl === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || activeEl === last)) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);

    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      for (const { el, inert, hidden } of muted) {
        if (inert === null) el.removeAttribute('inert');
        else el.setAttribute('inert', inert);
        if (hidden === null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', hidden);
      }
      if (lockScroll) {
        document.body.style.overflow = prevOverflow;
        document.body.style.paddingRight = prevPad;
      }
      const back =
        prevFocus && prevFocus.isConnected
          ? prevFocus
          : restoreFocusTo
            ? document.querySelector<HTMLElement>(restoreFocusTo)
            : null;
      back?.focus?.();
    };
  }, [active, panelRef]);
}

/* ---------------------------------------------------------------------------
   useOverlay — for surfaces that keep their own markup (a form as the panel,
   an aside as the drawer) and only want the contract.
   --------------------------------------------------------------------------- */

export interface UseOverlayOptions extends OverlayBehaviour {
  open: boolean;
  onClose: () => void;
}

export interface UseOverlayResult {
  /** False while this overlay is waiting for its turn at the slot. */
  granted: boolean;
  /** Render your markup only while this is true. */
  active: boolean;
  panelRef: RefObject<HTMLElement | null>;
  panelProps: {
    role: 'dialog' | 'presentation';
    'aria-modal'?: true;
    tabIndex: -1;
  };
  rootProps: {
    'data-pear-overlay-root': '';
    onClick: (e: ReactMouseEvent<HTMLElement>) => void;
  };
}

export function useOverlay(opts: UseOverlayOptions): UseOverlayResult {
  const { open, onClose, trap, lockScroll, inertBackground = true, initialFocus, restoreFocusTo } = opts;

  const [granted, setGranted] = useState(false);
  const keyRef = useRef<symbol>(Symbol('pear-overlay'));

  useEffect(() => {
    if (!open) return;
    const release = claimSlot({
      key: keyRef.current,
      grant: () => setGranted(true),
    });
    return () => {
      release();
      setGranted(false);
    };
  }, [open]);

  const panelRef = useRef<HTMLElement | null>(null);
  const active = open && granted;

  useOverlayBehaviour(active, panelRef, onClose, { trap, lockScroll, inertBackground, initialFocus, restoreFocusTo });

  return {
    granted,
    active,
    panelRef,
    panelProps: {
      role: 'dialog',
      ...(inertBackground ? ({ 'aria-modal': true } as const) : {}),
      tabIndex: -1,
    },
    rootProps: {
      'data-pear-overlay-root': '',
      onClick: (e: ReactMouseEvent<HTMLElement>) => {
        if (e.target === e.currentTarget) onClose();
      },
    },
  };
}

/* ---------------------------------------------------------------------------
   Overlay — the convenience component for the app's plain overlays.
   --------------------------------------------------------------------------- */

export interface OverlayProps extends OverlayBehaviour {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Class on the backdrop element — every existing overlay stylesheet keys off it. */
  className?: string;
  style?: CSSProperties;
  panelClassName?: string;
  panelStyle?: CSSProperties;
  panelId?: string;
  /** Id on the BACKDROP element (callers whose tests address the old root id). */
  rootId?: string;
  role?: 'dialog' | 'presentation';
  label?: string;
  labelledBy?: string;
  testId?: string;
  /** `data-testid` on the PANEL rather than the backdrop — for surfaces whose
   *  existing tests address the dialog itself (the chores week drawer). */
  panelTestId?: string;
  /** false keeps the node inline — anchored popovers that must stay inside
   *  their own positioning context (the compose menu). */
  portal?: boolean;
  dismissOnBackdrop?: boolean;
  /** Document-level dismissal for inline overlays, whose backdrop does not
   *  cover the page (the compose menu's original gap). */
  closeOnOutsidePointer?: boolean;
  /** Extra props merged onto the panel element (touch handlers) — the panel
   *  stays a plain div so callers keep their own behaviour hooks. */
  panelProps?: HTMLAttributes<HTMLElement>;
}

export function Overlay({
  open,
  onClose,
  children,
  className,
  style,
  panelClassName,
  panelStyle,
  panelId,
  rootId,
  role = 'dialog',
  label,
  labelledBy,
  testId,
  panelTestId,
  portal = true,
  dismissOnBackdrop = true,
  closeOnOutsidePointer = false,
  panelProps,
  ...behaviour
}: OverlayProps) {
  const modal = role === 'dialog';
  /* aria-modal is a PROMISE: it tells assistive tech that everything outside
     this dialog is unreachable. An inline overlay (the compose menu) cannot
     keep it — its backdrop does not cover the page and the app behind stays
     interactive — so the attribute is emitted only when the background is
     actually made inert. */
  const inertBackground = behaviour.inertBackground ?? portal;
  const { active, panelRef, rootProps } = useOverlay({
    open,
    onClose,
    ...behaviour,
    trap: behaviour.trap ?? modal,
    lockScroll: behaviour.lockScroll ?? portal,
    inertBackground,
  });

  useEffect(() => {
    if (!active || !closeOnOutsidePointer) return;
    const onDown = (e: Event) => {
      const t = e.target as Node | null;
      if (t && panelRef.current && !panelRef.current.contains(t)) onClose();
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
    };
  }, [active, closeOnOutsidePointer, onClose, panelRef]);

  if (!active) return null;

  /* Callers pass their own panel attributes (the tour's swipe handlers) —
     they land on the panel, never on the ARIA the primitive owns, and they
     never clobber className/style/id when the component's own prop is set. */
  const {
    style: panelExtraStyle,
    className: panelExtraClass,
    id: panelIdExtra,
    ...panelRest
  } = panelProps ?? {};

  const node = (
    <div
      {...rootProps}
      id={rootId}
      className={className}
      style={style}
      data-testid={testId}
      onClick={dismissOnBackdrop ? rootProps.onClick : undefined}
    >
      <div
        {...panelRest}
        ref={panelRef as RefObject<HTMLDivElement | null>}
        id={panelId ?? panelIdExtra}
        className={[panelClassName, panelExtraClass].filter(Boolean).join(' ') || undefined}
        style={panelExtraStyle ?? panelStyle}
        role={role}
        data-testid={panelTestId}
        aria-modal={modal && inertBackground ? true : undefined}
        aria-label={label}
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );

  return portal ? createPortal(node, document.body) : node;
}
